import { Progress, useHelmet } from '@agro-club/agroclub-shared'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { MovementsTabTypes, Warehouse, WarehouseStatuses } from 'modules/domain/warehouses/types'
import { endpoints } from 'modules/endpoints'
import { getPersonalNumberLabel } from 'modules/utils/numbers/formatPersonalNumber'
import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { CompanyLink } from 'views/components/CompanyLink/CompanyLink'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import * as Layout from 'views/layouts/NewLayout'
import WarehousesRoutes from 'views/pages/Warehouses/routes'
import * as Header from 'views/ui/Header/Header'
import APhone from 'views/ui/Phone/APhone'
import { ArchiveWarehouse } from './ArchiveWarehouse'
import { WarehouseDetailFilters } from './WarehouseDetailFilters'
import { MovementsGrouped } from './WarehouseDetailTable/MovementsGrouped/MovementsGrouped'
import { Tab, useTab, useTabsDataV2 } from 'hooks/useTabsDataV2'
import { AAntdTabs } from 'views/components/Analytics'
import { MovementsUngrouped } from './WarehouseDetailTable/MovementsUngrouped/MovementsUngrouped'

export const WarehouseDetail: FC = () => {
  const { id } = useParams()
  const { t } = useTranslation('warehouses')
  const [progress, warehouse, refetch] = useSingleEntity<Warehouse>(endpoints.warehouses(id))
  useHelmet({ title: `${t('warehouse')} - ${warehouse?.title}` })
  const { activeTab: activeTabParam } = useParams()
  const [activeTab, setActiveTab] = useState(activeTabParam || MovementsTabTypes.grouped)
  const navigate = useNavigate()

  const tabs: Record<string, Tab> = {}

  tabs[MovementsTabTypes.grouped] = useTab({
    label: t('detail.grouped'),
  })

  tabs[MovementsTabTypes.ungrouped] = useTab({
    label: t('detail.ungrouped'),
  })

  const { isFiltersLoaded } = useTabsDataV2({
    activeTab,
    tabs,
    independTabs: true,
  })

  const archiveOnSuccess = () => {
    refetch(true)
  }

  const showBidMoveMovements = useCallback((sale_bid_id: number) => {
    tabs[MovementsTabTypes.ungrouped].updateFilterState({ sale_bid_id })
    setActiveTab(MovementsTabTypes.ungrouped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    navigate(
      {
        pathname: generatePath(WarehousesRoutes.DetailWithTab, { id, activeTab }),
        search: location.search,
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  if (!warehouse) return null
  if (progress === Progress.WORK) return <ItemLoadingLayout id={id} />

  return (
    <APageWrapper page="WarehousesDetail" place="WarehousesDetail">
      <Layout.WrapperContent>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                {
                  path: WarehousesRoutes.List,
                  breadcrumbName: t('title'),
                },
                {
                  breadcrumbName: warehouse.title,
                },
              ]}
            />
            <ArchiveWarehouse refetch={archiveOnSuccess} status={warehouse.status as WarehouseStatuses} />
          </Layout.TopHeader>
          <Layout.PageName style={{ marginBottom: '8px' }}>{warehouse.title}</Layout.PageName>
          <Layout.DetailPageSubTitle>
            <div>
              {warehouse.owner.profile?.company && <CompanyLink company={warehouse.owner.profile?.company} />}{' '}
              {warehouse.owner.profile?.company?.inn &&
                `/ ${getPersonalNumberLabel()}: ${warehouse.owner.profile?.company?.inn} / `}{' '}
              {warehouse.owner.phone && <APhone phone={warehouse.owner.phone} />}
            </div>
            <div>{warehouse.address.address}</div>
          </Layout.DetailPageSubTitle>
          <WarehouseDetailFilters
            updateFilterState={tabs[activeTab].updateFilterState}
            listRequestParams={tabs[activeTab].params}
            clearTabState={tabs[activeTab].clearTabState}
            defaultListParams={tabs[activeTab].initialParams || {}}
            warehouseId={warehouse.id as unknown as number}
            activeTab={activeTab}
          />
        </Layout.Header>

        <AAntdTabs
          id="tabs"
          items={[
            {
              label: t('detail.grouped'),
              key: MovementsTabTypes.grouped,
              children: (
                <MovementsGrouped
                  listRequestParams={tabs[MovementsTabTypes.grouped].params}
                  setListRequestParams={tabs[MovementsTabTypes.grouped].setter}
                  isFiltersLoaded={isFiltersLoaded}
                  showBidMoveMovements={showBidMoveMovements}
                />
              ),
            },
            {
              label: t('detail.ungrouped'),
              key: MovementsTabTypes.ungrouped,
              children: (
                <MovementsUngrouped
                  listRequestParams={tabs[MovementsTabTypes.ungrouped].params}
                  setListRequestParams={tabs[MovementsTabTypes.ungrouped].setter}
                  isFiltersLoaded={isFiltersLoaded}
                />
              ),
            },
          ]}
          onChange={setActiveTab}
          activeKey={activeTab}
        />
      </Layout.WrapperContent>
    </APageWrapper>
  )
}
